import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Download, 
  Upload, 
  Printer, 
  Users, 
  History,
  Save,
  FileSpreadsheet,
  RefreshCw,
  Calculator,
  GripVertical,
  Trash2,
  Copy,
  Scissors,
  ClipboardPaste,
  Undo2,
  Redo2,
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown,
  ChevronRight,
  FileUp,
  Table,
  MoreHorizontal,
  PlusCircle,
  MinusCircle,
  ArrowUpDown,
  Search,
  Combine,
  SplitSquareHorizontal,
  Columns,
  Rows,
  Shield
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const PERMISSION_LABELS = {
  view: 'View',
  edit_cells: 'Edit Cells',
  edit_structure: 'Edit Structure',
  download: 'Download',
  print: 'Print',
  email: 'Email',
  import: 'Import',
  export: 'Export'
};

// Helper to convert column number to letter (1=A, 2=B, etc.)
function colNumToLetter(n) {
  let result = '';
  while (n > 0) {
    n--;
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }
  return result;
}

// Helper to convert column letter to number (A=1, B=2, etc.)
function colLetterToNum(col) {
  let result = 0;
  for (let i = 0; i < col.length; i++) {
    result = result * 26 + (col.charCodeAt(i) - 64);
  }
  return result;
}

// Parse cell reference (e.g., "A1" -> {col: "A", row: 1})
function parseCellRef(ref) {
  if (!ref) return null;
  const match = ref.match(/^([A-Za-z]+)(\d+)$/);
  if (!match) return null;
  return { col: match[1].toUpperCase(), row: parseInt(match[2]) };
}

// Formula Parser and Evaluator
class FormulaEngine {
  constructor(getCellValue, sheets) {
    this.getCellValue = getCellValue;
    this.sheets = sheets;
    this.cache = new Map();
  }

  evaluate(formula, currentSheet) {
    if (!formula || !formula.startsWith('=')) return formula;
    
    const cacheKey = `${currentSheet}:${formula}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const expr = formula.substring(1);
      const result = this.evaluateExpression(expr, currentSheet);
      this.cache.set(cacheKey, result);
      return result;
    } catch (e) {
      console.error('Formula error:', e);
      return '#ERROR!';
    }
  }

  evaluateExpression(expr, currentSheet) {
    // Handle SUM function
    if (expr.toUpperCase().startsWith('SUM(')) {
      return this.evaluateSum(expr, currentSheet);
    }
    
    // Handle AVERAGE function
    if (expr.toUpperCase().startsWith('AVERAGE(')) {
      return this.evaluateAverage(expr, currentSheet);
    }
    
    // Handle COUNT function
    if (expr.toUpperCase().startsWith('COUNT(')) {
      return this.evaluateCount(expr, currentSheet);
    }
    
    // Handle COUNTA function (count non-empty cells)
    if (expr.toUpperCase().startsWith('COUNTA(')) {
      return this.evaluateCountA(expr, currentSheet);
    }
    
    // Handle IF function
    if (expr.toUpperCase().startsWith('IF(')) {
      return this.evaluateIf(expr, currentSheet);
    }
    
    // Handle MAX function
    if (expr.toUpperCase().startsWith('MAX(')) {
      return this.evaluateMax(expr, currentSheet);
    }
    
    // Handle MIN function
    if (expr.toUpperCase().startsWith('MIN(')) {
      return this.evaluateMin(expr, currentSheet);
    }
    
    // Handle ROUND function
    if (expr.toUpperCase().startsWith('ROUND(')) {
      return this.evaluateRound(expr, currentSheet);
    }
    
    // Handle ABS function
    if (expr.toUpperCase().startsWith('ABS(')) {
      return this.evaluateAbs(expr, currentSheet);
    }
    
    // Handle CONCATENATE function
    if (expr.toUpperCase().startsWith('CONCATENATE(')) {
      return this.evaluateConcatenate(expr, currentSheet);
    }
    
    // Handle LEFT function
    if (expr.toUpperCase().startsWith('LEFT(')) {
      return this.evaluateLeft(expr, currentSheet);
    }
    
    // Handle RIGHT function
    if (expr.toUpperCase().startsWith('RIGHT(')) {
      return this.evaluateRight(expr, currentSheet);
    }
    
    // Handle LEN function
    if (expr.toUpperCase().startsWith('LEN(')) {
      return this.evaluateLen(expr, currentSheet);
    }
    
    // Handle UPPER function
    if (expr.toUpperCase().startsWith('UPPER(')) {
      return this.evaluateUpper(expr, currentSheet);
    }
    
    // Handle LOWER function
    if (expr.toUpperCase().startsWith('LOWER(')) {
      return this.evaluateLower(expr, currentSheet);
    }
    
    // Handle TRIM function
    if (expr.toUpperCase().startsWith('TRIM(')) {
      return this.evaluateTrim(expr, currentSheet);
    }
    
    // Handle TODAY function
    if (expr.toUpperCase() === 'TODAY()') {
      return new Date().toISOString().split('T')[0];
    }
    
    // Handle NOW function
    if (expr.toUpperCase() === 'NOW()') {
      return new Date().toISOString();
    }
    
    // Handle basic arithmetic with cell references
    return this.evaluateArithmetic(expr, currentSheet);
  }

  evaluateSum(expr, currentSheet) {
    const match = expr.match(/SUM\((.*)\)/i);
    if (!match) return 0;
    
    const values = this.getRangeValues(match[1], currentSheet);
    return values.reduce((sum, val) => sum + (typeof val === 'number' ? val : parseFloat(val) || 0), 0);
  }

  evaluateAverage(expr, currentSheet) {
    const match = expr.match(/AVERAGE\((.*)\)/i);
    if (!match) return 0;
    
    const values = this.getRangeValues(match[1], currentSheet).filter(v => typeof v === 'number' || !isNaN(parseFloat(v)));
    if (values.length === 0) return 0;
    const sum = values.reduce((s, val) => s + (typeof val === 'number' ? val : parseFloat(val) || 0), 0);
    return sum / values.length;
  }

  evaluateCount(expr, currentSheet) {
    const match = expr.match(/COUNT\((.*)\)/i);
    if (!match) return 0;
    
    const values = this.getRangeValues(match[1], currentSheet);
    return values.filter(v => typeof v === 'number' || !isNaN(parseFloat(v))).length;
  }

  evaluateCountA(expr, currentSheet) {
    const match = expr.match(/COUNTA\((.*)\)/i);
    if (!match) return 0;
    
    const values = this.getRangeValues(match[1], currentSheet);
    return values.filter(v => v !== null && v !== '' && v !== undefined).length;
  }

  evaluateMax(expr, currentSheet) {
    const match = expr.match(/MAX\((.*)\)/i);
    if (!match) return 0;
    
    const values = this.getRangeValues(match[1], currentSheet)
      .map(v => typeof v === 'number' ? v : parseFloat(v))
      .filter(v => !isNaN(v));
    return values.length > 0 ? Math.max(...values) : 0;
  }

  evaluateMin(expr, currentSheet) {
    const match = expr.match(/MIN\((.*)\)/i);
    if (!match) return 0;
    
    const values = this.getRangeValues(match[1], currentSheet)
      .map(v => typeof v === 'number' ? v : parseFloat(v))
      .filter(v => !isNaN(v));
    return values.length > 0 ? Math.min(...values) : 0;
  }

  evaluateRound(expr, currentSheet) {
    const match = expr.match(/ROUND\((.*)\)/i);
    if (!match) return 0;
    
    const args = match[1].split(',').map(a => a.trim());
    const value = this.evaluateValue(args[0], currentSheet);
    const decimals = args[1] ? parseInt(args[1]) : 0;
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return Math.round(numValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  evaluateAbs(expr, currentSheet) {
    const match = expr.match(/ABS\((.*)\)/i);
    if (!match) return 0;
    
    const value = this.evaluateValue(match[1], currentSheet);
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return Math.abs(numValue);
  }

  evaluateConcatenate(expr, currentSheet) {
    const match = expr.match(/CONCATENATE\((.*)\)/i);
    if (!match) return '';
    
    const args = this.splitArgs(match[1]);
    return args.map(arg => {
      const val = this.evaluateValue(arg.trim(), currentSheet);
      return val !== null && val !== undefined ? String(val) : '';
    }).join('');
  }

  evaluateLeft(expr, currentSheet) {
    const match = expr.match(/LEFT\((.*)\)/i);
    if (!match) return '';
    
    const args = match[1].split(',').map(a => a.trim());
    const text = String(this.evaluateValue(args[0], currentSheet) || '');
    const numChars = args[1] ? parseInt(args[1]) : 1;
    return text.substring(0, numChars);
  }

  evaluateRight(expr, currentSheet) {
    const match = expr.match(/RIGHT\((.*)\)/i);
    if (!match) return '';
    
    const args = match[1].split(',').map(a => a.trim());
    const text = String(this.evaluateValue(args[0], currentSheet) || '');
    const numChars = args[1] ? parseInt(args[1]) : 1;
    return text.substring(text.length - numChars);
  }

  evaluateLen(expr, currentSheet) {
    const match = expr.match(/LEN\((.*)\)/i);
    if (!match) return 0;
    
    const text = String(this.evaluateValue(match[1], currentSheet) || '');
    return text.length;
  }

  evaluateUpper(expr, currentSheet) {
    const match = expr.match(/UPPER\((.*)\)/i);
    if (!match) return '';
    
    const text = String(this.evaluateValue(match[1], currentSheet) || '');
    return text.toUpperCase();
  }

  evaluateLower(expr, currentSheet) {
    const match = expr.match(/LOWER\((.*)\)/i);
    if (!match) return '';
    
    const text = String(this.evaluateValue(match[1], currentSheet) || '');
    return text.toLowerCase();
  }

  evaluateTrim(expr, currentSheet) {
    const match = expr.match(/TRIM\((.*)\)/i);
    if (!match) return '';
    
    const text = String(this.evaluateValue(match[1], currentSheet) || '');
    return text.trim();
  }

  splitArgs(str) {
    const result = [];
    let depth = 0;
    let current = '';
    
    for (const char of str) {
      if (char === '(') depth++;
      else if (char === ')') depth--;
      
      if (char === ',' && depth === 0) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    if (current) result.push(current);
    return result;
  }

  evaluateIf(expr, currentSheet) {
    const match = expr.match(/IF\((.*)\)/i);
    if (!match) return '';
    
    const parts = this.splitIfArgs(match[1]);
    if (parts.length < 3) return '';
    
    const condition = this.evaluateCondition(parts[0], currentSheet);
    return condition ? this.evaluateValue(parts[1], currentSheet) : this.evaluateValue(parts[2], currentSheet);
  }

  splitIfArgs(str) {
    const result = [];
    let depth = 0;
    let current = '';
    
    for (const char of str) {
      if (char === '(') depth++;
      else if (char === ')') depth--;
      else if (char === ',' && depth === 0) {
        result.push(current.trim());
        current = '';
        continue;
      }
      current += char;
    }
    if (current) result.push(current.trim());
    return result;
  }

  evaluateCondition(cond, currentSheet) {
    // Handle comparison operators
    const operators = ['>=', '<=', '<>', '!=', '=', '>', '<'];
    for (const op of operators) {
      if (cond.includes(op)) {
        const [left, right] = cond.split(op).map(s => s.trim());
        const leftVal = this.evaluateValue(left, currentSheet);
        const rightVal = this.evaluateValue(right, currentSheet);
        
        switch (op) {
          case '>=': return leftVal >= rightVal;
          case '<=': return leftVal <= rightVal;
          case '<>':
          case '!=': return leftVal !== rightVal;
          case '=': return leftVal === rightVal;
          case '>': return leftVal > rightVal;
          case '<': return leftVal < rightVal;
        }
      }
    }
    return false;
  }

  evaluateArithmetic(expr, currentSheet) {
    // Replace cell references with values
    let processedExpr = expr;
    
    // Handle cross-sheet references first (e.g., 'Sheet Name'!A1)
    const crossSheetRegex = /'([^']+)'!([A-Z]+\d+)/gi;
    processedExpr = processedExpr.replace(crossSheetRegex, (match, sheetName, cellRef) => {
      const value = this.getCellValueFromSheet(sheetName, cellRef);
      return typeof value === 'number' ? value : (parseFloat(value) || 0);
    });
    
    // Handle simple cell references (A1, B2, etc.)
    const cellRefRegex = /([A-Z]+\d+)/gi;
    processedExpr = processedExpr.replace(cellRefRegex, (match, cellRef) => {
      const value = this.getCellValue(cellRef);
      return typeof value === 'number' ? value : (parseFloat(value) || 0);
    });
    
    // Evaluate the arithmetic expression
    try {
      // Simple safe eval for basic arithmetic
      const sanitized = processedExpr.replace(/[^0-9+\-*/().]/g, '');
      if (sanitized !== processedExpr) {
        // Expression contains non-arithmetic characters
        return processedExpr;
      }
      return Function(`"use strict"; return (${processedExpr})`)();
    } catch (e) {
      return '#ERROR!';
    }
  }

  evaluateValue(val, currentSheet) {
    if (val === undefined || val === null) return 0;
    val = val.toString().trim();
    
    // Check if it's a quoted string
    if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
      return val.slice(1, -1);
    }
    
    // Check if it's a number
    if (!isNaN(parseFloat(val)) && isFinite(val)) {
      return parseFloat(val);
    }
    
    // Check if it's a cell reference
    if (/^[A-Z]+\d+$/i.test(val)) {
      const cellVal = this.getCellValue(val);
      return typeof cellVal === 'number' ? cellVal : (parseFloat(cellVal) || 0);
    }
    
    // Check if it's a cross-sheet reference
    const crossSheetMatch = val.match(/'([^']+)'!([A-Z]+\d+)/i);
    if (crossSheetMatch) {
      return this.getCellValueFromSheet(crossSheetMatch[1], crossSheetMatch[2]);
    }
    
    return val;
  }

  getRangeValues(rangeExpr, currentSheet) {
    const values = [];
    
    // Handle multiple ranges separated by commas
    const ranges = rangeExpr.split(',').map(r => r.trim());
    
    for (const range of ranges) {
      // Check for cross-sheet reference
      let sheetName = currentSheet;
      let cellRange = range;
      
      const crossSheetMatch = range.match(/'([^']+)'!(.*)/);
      if (crossSheetMatch) {
        sheetName = crossSheetMatch[1];
        cellRange = crossSheetMatch[2];
      }
      
      // Check if it's a range (A1:B5) or single cell (A1)
      if (cellRange.includes(':')) {
        const [start, end] = cellRange.split(':');
        const startRef = parseCellRef(start);
        const endRef = parseCellRef(end);
        
        if (startRef && endRef) {
          const startCol = colLetterToNum(startRef.col);
          const endCol = colLetterToNum(endRef.col);
          
          for (let row = startRef.row; row <= endRef.row; row++) {
            for (let col = startCol; col <= endCol; col++) {
              const cellRef = `${colNumToLetter(col)}${row}`;
              const value = sheetName === currentSheet 
                ? this.getCellValue(cellRef)
                : this.getCellValueFromSheet(sheetName, cellRef);
              if (value !== null && value !== undefined) {
                values.push(value);
              }
            }
          }
        }
      } else {
        // Single cell
        const value = sheetName === currentSheet 
          ? this.getCellValue(cellRange)
          : this.getCellValueFromSheet(sheetName, cellRange);
        if (value !== null && value !== undefined) {
          values.push(value);
        }
      }
    }
    
    return values;
  }

  getCellValueFromSheet(sheetName, cellRef) {
    const sheet = this.sheets?.find(s => s.name === sheetName);
    if (!sheet || !sheet.cells) return 0;
    const cell = sheet.cells[cellRef.toUpperCase()];
    if (!cell) return 0;
    
    // Return the value - should be computed value from Excel
    const value = cell.v;
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    
    // Try to parse as number
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) return numValue;
    
    return value;
  }

  clearCache() {
    this.cache.clear();
  }
}

export default function AccountsExcelPage() {
  const { api, user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [allSheetsData, setAllSheetsData] = useState({});
  const [activeSheet, setActiveSheet] = useState(null);
  const [sheetData, setSheetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editedCells, setEditedCells] = useState({});
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectionRange, setSelectionRange] = useState(null);
  const [formulaBarValue, setFormulaBarValue] = useState('');
  const [columnWidths, setColumnWidths] = useState({});
  const [rowHeights, setRowHeights] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [clipboard, setClipboard] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Dialogs
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [addRowDialogOpen, setAddRowDialogOpen] = useState(false);
  const [addColDialogOpen, setAddColDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [autofitDialogOpen, setAutofitDialogOpen] = useState(false);
  
  // Merge/Autofit state
  const [mergeStartCell, setMergeStartCell] = useState('');
  const [mergeEndCell, setMergeEndCell] = useState('');
  const [autofitType, setAutofitType] = useState('all_columns');
  const [autofitTarget, setAutofitTarget] = useState('');
  const [merging, setMerging] = useState(false);
  const [autofitting, setAutofitting] = useState(false);
  
  // Drag-to-fill state
  const [isDraggingFill, setIsDraggingFill] = useState(false);
  const [fillStartCell, setFillStartCell] = useState(null);
  const [fillEndCell, setFillEndCell] = useState(null);
  const [fillPreviewCells, setFillPreviewCells] = useState([]);
  
  // Selection range state
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  
  const [versions, setVersions] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  
  const fileInputRef = useRef(null);
  const tableContainerRef = useRef(null);
  const resizingCol = useRef(null);
  const resizingRow = useRef(null);
  const fillHandleRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const startWidth = useRef(0);
  const startHeight = useRef(0);

  const isMainAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk' || user?.is_main_admin;

  // Formula engine instance
  const formulaEngine = useMemo(() => {
    // Create a function that returns the EVALUATED value of a cell
    // This is critical - if a cell has a formula, we must evaluate it first
    const getCellVal = (cellRef, evaluatingRefs = new Set()) => {
      // Prevent circular references
      if (evaluatingRefs.has(cellRef)) {
        return 0; // Break circular reference
      }
      
      // Check for edited cells first
      if (editedCells[cellRef] !== undefined) {
        if (editedCells[cellRef].formula) {
          // Need to evaluate this formula
          evaluatingRefs.add(cellRef);
          // Don't use the engine recursively here - return raw value for formulas
          return editedCells[cellRef].value ?? 0;
        }
        return editedCells[cellRef].value ?? 0;
      }
      
      // Get cell from sheet data
      const cell = sheetData?.cells?.[cellRef];
      if (!cell) return 0;
      
      // If cell has a formula, we need its computed value
      // The backend should have stored the computed value in .v
      // If .v is null but .f exists, return 0 (needs recalculation)
      if (cell.f && (cell.v === null || cell.v === undefined)) {
        // Formula exists but no cached value - return 0 for now
        return 0;
      }
      
      // Return the value (which should be the computed value from Excel import)
      const value = cell.v;
      if (value === null || value === undefined) return 0;
      if (typeof value === 'number') return value;
      
      // Try to parse as number
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) return numValue;
      
      return value;
    };
    return new FormulaEngine(getCellVal, Object.values(allSheetsData));
  }, [sheetData, editedCells, allSheetsData]);

  useEffect(() => {
    fetchSheets();
    fetchUsers();
  }, []);

  const fetchSheets = async () => {
    try {
      const response = await api.get('/accounts-excel/sheets');
      setSheets(response.data);
      if (response.data.length > 0 && !activeSheet) {
        setActiveSheet(response.data[0].id);
      }
      
      // Pre-fetch all sheets data for cross-sheet formula evaluation
      const sheetsData = {};
      for (const sheet of response.data) {
        try {
          const sheetResponse = await api.get(`/accounts-excel/sheets/${sheet.id}`);
          sheetsData[sheet.id] = sheetResponse.data;
        } catch (e) {
          console.error(`Failed to fetch sheet ${sheet.id}:`, e);
        }
      }
      setAllSheetsData(sheetsData);
    } catch (error) {
      console.error('Failed to fetch sheets:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load account sheets');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/staff');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    if (activeSheet) {
      fetchSheetData(activeSheet);
    }
  }, [activeSheet]);

  const fetchSheetData = async (sheetId) => {
    try {
      setLoading(true);
      const response = await api.get(`/accounts-excel/sheets/${sheetId}`);
      setSheetData(response.data);
      setEditedCells({});
      setSelectedCell(null);
      setFormulaBarValue('');
      formulaEngine.clearCache();
    } catch (error) {
      console.error('Failed to fetch sheet data:', error);
      toast.error('Failed to load sheet data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    if (!activeSheet) return;
    try {
      const response = await api.get(`/accounts-excel/sheets/${activeSheet}/permissions`);
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  const fetchVersions = async () => {
    if (!activeSheet) return;
    try {
      const response = await api.get(`/accounts-excel/sheets/${activeSheet}/versions`);
      setVersions(response.data);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    }
  };

  const hasPermission = (permission) => {
    if (isMainAdmin) return true;
    return sheetData?.user_permissions?.includes(permission);
  };

  // Initialize sheets from Excel
  const handleInitialize = async () => {
    if (!confirm('This will reinitialize all sheets from the ICRCL_Account.xlsx file. Continue?')) return;
    
    setInitializing(true);
    try {
      const response = await api.post('/accounts-excel/sheets/initialize-from-excel');
      toast.success(response.data.message);
      fetchSheets();
    } catch (error) {
      console.error('Failed to initialize:', error);
      toast.error('Failed to initialize sheets from Excel');
    } finally {
      setInitializing(false);
    }
  };

  // Upload new Excel file
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.post('/accounts-excel/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded successfully! Reinitializing sheets...');
      await handleInitialize();
      setUploadDialogOpen(false);
    } catch (error) {
      console.error('Failed to upload:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Get cell value with formula evaluation
  const getCellValue = useCallback((cellRef) => {
    if (editedCells[cellRef]?.value !== undefined) {
      return editedCells[cellRef].value;
    }
    const cell = sheetData?.cells?.[cellRef];
    return cell?.v ?? '';
  }, [editedCells, sheetData]);

  // Get cell formula
  const getCellFormula = useCallback((cellRef) => {
    if (editedCells[cellRef]?.formula !== undefined) {
      return editedCells[cellRef].formula;
    }
    const cell = sheetData?.cells?.[cellRef];
    return cell?.f ?? null;
  }, [editedCells, sheetData]);

  // Get display value (evaluate formula if present)
  const getCellDisplay = useCallback((cellRef) => {
    const formula = getCellFormula(cellRef);
    let value;
    
    // First, always try to get the pre-computed value from the backend
    // The backend already evaluates cross-sheet formulas correctly
    const rawValue = getCellValue(cellRef);
    
    if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
      // Use the pre-computed value from the backend
      value = rawValue;
    } else if (formula) {
      // Only re-evaluate if no pre-computed value exists
      // This handles locally edited formulas
      value = formulaEngine.evaluate(formula, sheetData?.name);
    } else {
      value = rawValue;
    }
    
    // Handle empty/null values
    if (value === null || value === undefined || value === '') return '';
    if (value === '#ERROR!') return value;
    
    // Format numbers properly
    if (typeof value === 'number') {
      // Check if it's a very small number (close to 0)
      if (Math.abs(value) < 0.001) return '0';
      // Check if it's effectively an integer
      if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 0.001) {
        return Math.round(value).toString();
      }
      // Format with 2 decimal places for decimals
      return value.toFixed(2);
    }
    
    // Try to parse string values as numbers for display
    if (typeof value === 'string') {
      const numVal = parseFloat(value);
      if (!isNaN(numVal)) {
        if (Number.isInteger(numVal)) return numVal.toString();
        return numVal.toFixed(2);
      }
    }
    
    return String(value);
  }, [getCellValue, getCellFormula, formulaEngine, sheetData?.name]);

  // Handle cell selection
  const handleCellSelect = useCallback((cellRef) => {
    setSelectedCell(cellRef);
    const formula = getCellFormula(cellRef);
    const value = getCellValue(cellRef);
    setFormulaBarValue(formula || (value !== null && value !== undefined ? value.toString() : ''));
  }, [getCellFormula, getCellValue]);

  // Handle cell edit
  const handleCellChange = useCallback((cellRef, newValue) => {
    if (!hasPermission('edit_cells')) {
      toast.error('You do not have permission to edit cells');
      return;
    }
    
    // Save to undo stack
    const prevValue = {
      cell: cellRef,
      value: getCellValue(cellRef),
      formula: getCellFormula(cellRef)
    };
    setUndoStack(prev => [...prev, prevValue]);
    setRedoStack([]);
    
    const update = {};
    if (newValue && newValue.toString().startsWith('=')) {
      update.formula = newValue;
      update.value = null;
    } else {
      update.value = newValue;
      update.formula = null;
    }
    
    setEditedCells(prev => ({
      ...prev,
      [cellRef]: update
    }));
    
    // Clear formula cache to recalculate dependent cells
    formulaEngine.clearCache();
  }, [hasPermission, getCellValue, getCellFormula, formulaEngine]);

  // Handle formula bar change
  const handleFormulaBarChange = useCallback((value) => {
    setFormulaBarValue(value);
    if (selectedCell) {
      handleCellChange(selectedCell, value);
    }
  }, [selectedCell, handleCellChange]);

  // Handle formula bar submit (Enter key)
  const handleFormulaBarSubmit = useCallback(() => {
    if (selectedCell) {
      // Move to next row
      const ref = parseCellRef(selectedCell);
      if (ref) {
        const nextCell = `${ref.col}${ref.row + 1}`;
        handleCellSelect(nextCell);
      }
    }
  }, [selectedCell, handleCellSelect]);

  // Undo
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    
    const lastChange = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    
    // Save current state to redo
    setRedoStack(prev => [...prev, {
      cell: lastChange.cell,
      value: getCellValue(lastChange.cell),
      formula: getCellFormula(lastChange.cell)
    }]);
    
    // Restore previous state
    setEditedCells(prev => ({
      ...prev,
      [lastChange.cell]: {
        value: lastChange.value,
        formula: lastChange.formula
      }
    }));
    
    formulaEngine.clearCache();
  }, [undoStack, getCellValue, getCellFormula, formulaEngine]);

  // Redo
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    
    const lastRedo = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    
    setUndoStack(prev => [...prev, {
      cell: lastRedo.cell,
      value: getCellValue(lastRedo.cell),
      formula: getCellFormula(lastRedo.cell)
    }]);
    
    setEditedCells(prev => ({
      ...prev,
      [lastRedo.cell]: {
        value: lastRedo.value,
        formula: lastRedo.formula
      }
    }));
    
    formulaEngine.clearCache();
  }, [redoStack, getCellValue, getCellFormula, formulaEngine]);

  // Copy
  const handleCopy = useCallback(() => {
    if (!selectedCell) return;
    setClipboard({
      cell: selectedCell,
      value: getCellValue(selectedCell),
      formula: getCellFormula(selectedCell)
    });
    toast.success('Cell copied');
  }, [selectedCell, getCellValue, getCellFormula]);

  // Paste
  const handlePaste = useCallback(() => {
    if (!selectedCell || !clipboard) return;
    handleCellChange(selectedCell, clipboard.formula || clipboard.value);
    toast.success('Cell pasted');
  }, [selectedCell, clipboard, handleCellChange]);

  // Delete cell content
  const handleDelete = useCallback(() => {
    if (!selectedCell) return;
    handleCellChange(selectedCell, '');
  }, [selectedCell, handleCellChange]);

  // ================== DRAG-TO-FILL FUNCTIONALITY ==================
  
  // Adjust formula references when filling (e.g., =A1 becomes =A2 when filled down)
  const adjustFormulaReferences = useCallback((formula, rowOffset, colOffset) => {
    if (!formula || !formula.startsWith('=')) return formula;
    
    // Match cell references like A1, B2, AA10, etc.
    // Handles both absolute ($A$1) and relative (A1) references
    const cellRefRegex = /(\$?)([A-Z]+)(\$?)(\d+)/gi;
    
    return formula.replace(cellRefRegex, (match, colAbs, col, rowAbs, row) => {
      let newCol = col;
      let newRow = parseInt(row);
      
      // Only adjust if not absolute reference
      if (!colAbs) {
        const colNum = colLetterToNum(col);
        const newColNum = colNum + colOffset;
        if (newColNum > 0) {
          newCol = colNumToLetter(newColNum);
        }
      }
      
      if (!rowAbs) {
        newRow = newRow + rowOffset;
        if (newRow < 1) newRow = 1;
      }
      
      return `${colAbs}${newCol}${rowAbs}${newRow}`;
    });
  }, []);

  // Calculate fill values for series (numbers, dates, formulas)
  const calculateFillValues = useCallback((sourceCell, targetCells) => {
    const sourceFormula = getCellFormula(sourceCell);
    const sourceValue = getCellValue(sourceCell);
    const sourceRef = parseCellRef(sourceCell);
    
    const fillValues = [];
    
    for (const targetCell of targetCells) {
      const targetRef = parseCellRef(targetCell);
      const rowOffset = targetRef.row - sourceRef.row;
      const colOffset = colLetterToNum(targetRef.col) - colLetterToNum(sourceRef.col);
      
      if (sourceFormula) {
        // Adjust formula references
        const adjustedFormula = adjustFormulaReferences(sourceFormula, rowOffset, colOffset);
        fillValues.push({ cell: targetCell, formula: adjustedFormula, value: null });
      } else if (typeof sourceValue === 'number' || !isNaN(parseFloat(sourceValue))) {
        // For numbers, increment based on position
        const numValue = typeof sourceValue === 'number' ? sourceValue : parseFloat(sourceValue);
        const increment = rowOffset !== 0 ? rowOffset : colOffset;
        fillValues.push({ cell: targetCell, value: numValue + increment, formula: null });
      } else {
        // For text, just copy
        fillValues.push({ cell: targetCell, value: sourceValue, formula: null });
      }
    }
    
    return fillValues;
  }, [getCellFormula, getCellValue, adjustFormulaReferences]);

  // Start drag-to-fill
  const handleFillHandleMouseDown = useCallback((e) => {
    if (!selectedCell || !hasPermission('edit_cells')) return;
    e.preventDefault();
    e.stopPropagation();
    
    setIsDraggingFill(true);
    setFillStartCell(selectedCell);
    setFillEndCell(selectedCell);
    setFillPreviewCells([]);
  }, [selectedCell, hasPermission]);

  // Update fill preview during drag
  const handleFillMouseMove = useCallback((e, cellRef) => {
    if (!isDraggingFill || !fillStartCell) return;
    
    setFillEndCell(cellRef);
    
    // Calculate cells in fill range
    const startRef = parseCellRef(fillStartCell);
    const endRef = parseCellRef(cellRef);
    
    if (!startRef || !endRef) return;
    
    const startRow = Math.min(startRef.row, endRef.row);
    const endRow = Math.max(startRef.row, endRef.row);
    const startColNum = Math.min(colLetterToNum(startRef.col), colLetterToNum(endRef.col));
    const endColNum = Math.max(colLetterToNum(startRef.col), colLetterToNum(endRef.col));
    
    const previewCells = [];
    for (let row = startRow; row <= endRow; row++) {
      for (let colNum = startColNum; colNum <= endColNum; colNum++) {
        const cellRef = `${colNumToLetter(colNum)}${row}`;
        if (cellRef !== fillStartCell) {
          previewCells.push(cellRef);
        }
      }
    }
    
    setFillPreviewCells(previewCells);
  }, [isDraggingFill, fillStartCell]);

  // Complete fill operation
  const handleFillMouseUp = useCallback(() => {
    if (!isDraggingFill || !fillStartCell || fillPreviewCells.length === 0) {
      setIsDraggingFill(false);
      setFillStartCell(null);
      setFillEndCell(null);
      setFillPreviewCells([]);
      return;
    }
    
    // Calculate and apply fill values
    const fillValues = calculateFillValues(fillStartCell, fillPreviewCells);
    
    // Save to undo stack
    const undoData = fillPreviewCells.map(cell => ({
      cell,
      value: getCellValue(cell),
      formula: getCellFormula(cell)
    }));
    setUndoStack(prev => [...prev, ...undoData]);
    setRedoStack([]);
    
    // Apply fill values
    const updates = {};
    for (const fill of fillValues) {
      updates[fill.cell] = { value: fill.value, formula: fill.formula };
    }
    
    setEditedCells(prev => ({ ...prev, ...updates }));
    formulaEngine.clearCache();
    
    toast.success(`Filled ${fillPreviewCells.length} cells`);
    
    // Reset fill state
    setIsDraggingFill(false);
    setFillStartCell(null);
    setFillEndCell(null);
    setFillPreviewCells([]);
  }, [isDraggingFill, fillStartCell, fillPreviewCells, calculateFillValues, getCellValue, getCellFormula, formulaEngine]);

  // Global mouse up handler for fill
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDraggingFill) {
        handleFillMouseUp();
      }
    };
    
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDraggingFill, handleFillMouseUp]);

  // Check if a cell is in the fill preview
  const isFillPreviewCell = useCallback((cellRef) => {
    return fillPreviewCells.includes(cellRef);
  }, [fillPreviewCells]);

  // ================== END DRAG-TO-FILL ==================

  // Save changes
  const saveChanges = async () => {
    if (Object.keys(editedCells).length === 0) return;
    
    setSaving(true);
    try {
      const updates = Object.entries(editedCells).map(([cell, data]) => ({
        cell: cell,
        value: data.value,
        formula: data.formula || null
      }));
      
      await api.put(`/accounts-excel/sheets/${activeSheet}/cells`, { updates });
      toast.success('Changes saved successfully');
      setEditedCells({});
      setUndoStack([]);
      setRedoStack([]);
      fetchSheetData(activeSheet);
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Column resize handlers
  const handleColumnResizeStart = (col, e) => {
    e.preventDefault();
    resizingCol.current = col;
    startX.current = e.clientX;
    startWidth.current = columnWidths[col] || 100;
    
    document.addEventListener('mousemove', handleColumnResizeMove);
    document.addEventListener('mouseup', handleColumnResizeEnd);
  };

  const handleColumnResizeMove = (e) => {
    if (!resizingCol.current) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(50, startWidth.current + diff);
    setColumnWidths(prev => ({
      ...prev,
      [resizingCol.current]: newWidth
    }));
  };

  const handleColumnResizeEnd = () => {
    resizingCol.current = null;
    document.removeEventListener('mousemove', handleColumnResizeMove);
    document.removeEventListener('mouseup', handleColumnResizeEnd);
  };

  // Row resize handlers
  const handleRowResizeStart = (row, e) => {
    e.preventDefault();
    resizingRow.current = row;
    startY.current = e.clientY;
    startHeight.current = rowHeights[row] || 32;
    
    document.addEventListener('mousemove', handleRowResizeMove);
    document.addEventListener('mouseup', handleRowResizeEnd);
  };

  const handleRowResizeMove = (e) => {
    if (!resizingRow.current) return;
    const diff = e.clientY - startY.current;
    const newHeight = Math.max(24, startHeight.current + diff);
    setRowHeights(prev => ({
      ...prev,
      [resizingRow.current]: newHeight
    }));
  };

  const handleRowResizeEnd = () => {
    resizingRow.current = null;
    document.removeEventListener('mousemove', handleRowResizeMove);
    document.removeEventListener('mouseup', handleRowResizeEnd);
  };

  // Add row
  const handleAddRow = async () => {
    try {
      await api.post(`/accounts-excel/sheets/${activeSheet}/add-row`);
      toast.success('Row added');
      fetchSheetData(activeSheet);
    } catch (error) {
      toast.error('Failed to add row');
    }
  };

  // Add column
  const handleAddColumn = async () => {
    try {
      await api.post(`/accounts-excel/sheets/${activeSheet}/add-column`);
      toast.success('Column added');
      fetchSheetData(activeSheet);
    } catch (error) {
      toast.error('Failed to add column');
    }
  };

  // Export
  const handleExport = async () => {
    if (!hasPermission('download')) {
      toast.error('You do not have permission to download this sheet');
      return;
    }
    
    try {
      const response = await api.get(`/accounts-excel/sheets/${activeSheet}/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ICRCL_${sheetData?.name || 'Sheet'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sheet downloaded successfully');
    } catch (error) {
      toast.error('Failed to export sheet');
    }
  };

  const handlePrint = () => {
    if (!hasPermission('print')) {
      toast.error('You do not have permission to print this sheet');
      return;
    }
    window.print();
  };

  // Merge cells function (Main Admin only)
  const handleMergeCells = async () => {
    if (!isMainAdmin) {
      toast.error('Only Md Ashraful Alam Sujan can merge cells');
      return;
    }
    
    if (!mergeStartCell || !mergeEndCell) {
      toast.error('Please specify start and end cells');
      return;
    }
    
    setMerging(true);
    try {
      await api.post(`/accounts-excel/sheets/${activeSheet}/merge`, {
        start_cell: mergeStartCell.toUpperCase(),
        end_cell: mergeEndCell.toUpperCase(),
        keep_value: 'top_left'
      });
      toast.success('Cells merged successfully');
      setMergeDialogOpen(false);
      setMergeStartCell('');
      setMergeEndCell('');
      // Refresh sheet data
      const response = await api.get(`/accounts-excel/sheets/${activeSheet}`);
      setSheetData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge cells');
    } finally {
      setMerging(false);
    }
  };

  // Unmerge cells function (Main Admin only)
  const handleUnmergeCells = async (cell) => {
    if (!isMainAdmin) {
      toast.error('Only Md Ashraful Alam Sujan can unmerge cells');
      return;
    }
    
    try {
      await api.post(`/accounts-excel/sheets/${activeSheet}/unmerge`, {
        cell: cell.toUpperCase()
      });
      toast.success('Cells unmerged successfully');
      // Refresh sheet data
      const response = await api.get(`/accounts-excel/sheets/${activeSheet}`);
      setSheetData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unmerge cells');
    }
  };

  // Autofit function (Main Admin only)
  const handleAutofit = async () => {
    if (!isMainAdmin) {
      toast.error('Only Md Ashraful Alam Sujan can autofit columns/rows');
      return;
    }
    
    setAutofitting(true);
    try {
      await api.post(`/accounts-excel/sheets/${activeSheet}/autofit`, {
        type: autofitType,
        target: autofitTarget || null
      });
      toast.success('Autofit applied successfully');
      setAutofitDialogOpen(false);
      setAutofitTarget('');
      // Refresh sheet data
      const response = await api.get(`/accounts-excel/sheets/${activeSheet}`);
      setSheetData(response.data);
      // Update local column widths
      if (response.data.column_widths) {
        setColumnWidths(response.data.column_widths);
      }
      if (response.data.row_heights) {
        setRowHeights(response.data.row_heights);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to autofit');
    } finally {
      setAutofitting(false);
    }
  };

  // Open merge dialog with selection if available
  const openMergeDialog = () => {
    if (selectionRange) {
      setMergeStartCell(selectionRange.start);
      setMergeEndCell(selectionRange.end);
    } else if (selectedCell) {
      setMergeStartCell(selectedCell);
      setMergeEndCell('');
    }
    setMergeDialogOpen(true);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 's':
            e.preventDefault();
            if (Object.keys(editedCells).length > 0) saveChanges();
            break;
          case 'z':
            e.preventDefault();
            if (e.shiftKey) handleRedo();
            else handleUndo();
            break;
          case 'y':
            e.preventDefault();
            handleRedo();
            break;
          case 'c':
            if (selectedCell) {
              e.preventDefault();
              handleCopy();
            }
            break;
          case 'v':
            if (selectedCell && clipboard) {
              e.preventDefault();
              handlePaste();
            }
            break;
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedCell && document.activeElement.tagName !== 'INPUT') {
          e.preventDefault();
          handleDelete();
        }
      } else if (e.key === 'Enter' && selectedCell) {
        const ref = parseCellRef(selectedCell);
        if (ref) {
          setSelectedCell(`${ref.col}${ref.row + 1}`);
        }
      } else if (e.key === 'Tab' && selectedCell) {
        e.preventDefault();
        const ref = parseCellRef(selectedCell);
        if (ref) {
          const nextCol = colLetterToNum(ref.col) + 1;
          setSelectedCell(`${colNumToLetter(nextCol)}${ref.row}`);
        }
      } else if (e.key === 'ArrowUp' && selectedCell && document.activeElement.tagName !== 'INPUT') {
        const ref = parseCellRef(selectedCell);
        if (ref && ref.row > 1) {
          setSelectedCell(`${ref.col}${ref.row - 1}`);
        }
      } else if (e.key === 'ArrowDown' && selectedCell && document.activeElement.tagName !== 'INPUT') {
        const ref = parseCellRef(selectedCell);
        if (ref) {
          setSelectedCell(`${ref.col}${ref.row + 1}`);
        }
      } else if (e.key === 'ArrowLeft' && selectedCell && document.activeElement.tagName !== 'INPUT') {
        const ref = parseCellRef(selectedCell);
        if (ref) {
          const colNum = colLetterToNum(ref.col);
          if (colNum > 1) {
            setSelectedCell(`${colNumToLetter(colNum - 1)}${ref.row}`);
          }
        }
      } else if (e.key === 'ArrowRight' && selectedCell && document.activeElement.tagName !== 'INPUT') {
        const ref = parseCellRef(selectedCell);
        if (ref) {
          const nextCol = colLetterToNum(ref.col) + 1;
          setSelectedCell(`${colNumToLetter(nextCol)}${ref.row}`);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCell, editedCells, clipboard, handleUndo, handleRedo, handleCopy, handlePaste, handleDelete, saveChanges]);

  // Generate grid
  const maxRow = sheetData?.max_row || 100;
  const maxCol = sheetData?.max_col || 20;
  
  const columnHeaders = useMemo(() => {
    const headers = [];
    for (let c = 1; c <= maxCol; c++) {
      headers.push(colNumToLetter(c));
    }
    return headers;
  }, [maxCol]);

  // Filter rows based on search
  const visibleRows = useMemo(() => {
    if (!searchTerm) return Array.from({ length: Math.min(maxRow, 1000) }, (_, i) => i + 1);
    
    const matchingRows = [];
    for (let row = 1; row <= Math.min(maxRow, 1000); row++) {
      for (let col = 1; col <= maxCol; col++) {
        const cellRef = `${colNumToLetter(col)}${row}`;
        const value = getCellDisplay(cellRef);
        if (value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())) {
          matchingRows.push(row);
          break;
        }
      }
    }
    return matchingRows;
  }, [searchTerm, maxRow, maxCol, getCellDisplay]);

  if (loading && !sheetData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4" data-testid="access-denied">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <Shield className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 text-center max-w-md">
          Only Directors can view the Accounts Excel sheets. Finance staff and other roles do not have access to this section.
        </p>
        <Button onClick={() => window.history.back()} variant="outline">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="accounts-excel-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-2">
            <FileSpreadsheet className="w-7 h-7" />
            ICRCL Accounts
          </h1>
          <p className="text-slate-500 mt-1">Financial tracking with live formula calculations</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {Object.keys(editedCells).length > 0 && (
            <Button 
              onClick={saveChanges} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-changes-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : `Save (${Object.keys(editedCells).length})`}
            </Button>
          )}
          
          {/* Undo/Redo */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleUndo}
            disabled={undoStack.length === 0}
            title="Undo (Ctrl+Z)"
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            title="Redo (Ctrl+Y)"
          >
            <Redo2 className="w-4 h-4" />
          </Button>
          
          {isMainAdmin && (
            <>
              <Button 
                variant="outline" 
                onClick={() => setUploadDialogOpen(true)}
                data-testid="upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Excel
              </Button>
              <Button 
                variant="outline" 
                onClick={handleInitialize}
                disabled={initializing}
                data-testid="reinitialize-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${initializing ? 'animate-spin' : ''}`} />
                {initializing ? 'Loading...' : 'Reload'}
              </Button>
              
              {/* Merge Cells Button */}
              <Button 
                variant="outline" 
                onClick={openMergeDialog}
                data-testid="merge-cells-btn"
                title="Merge Cells"
              >
                <Combine className="w-4 h-4 mr-2" />
                Merge
              </Button>
              
              {/* Autofit Button */}
              <Button 
                variant="outline" 
                onClick={() => setAutofitDialogOpen(true)}
                data-testid="autofit-btn"
                title="Autofit Columns/Rows"
              >
                <Columns className="w-4 h-4 mr-2" />
                Autofit
              </Button>
            </>
          )}
          
          {hasPermission('download') && (
            <Button variant="outline" onClick={handleExport} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          
          {hasPermission('print') && (
            <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          )}
          
          {isMainAdmin && (
            <>
              <Button 
                variant="outline" 
                onClick={() => { fetchPermissions(); setPermissionsDialogOpen(true); }}
                data-testid="manage-permissions-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Permissions
              </Button>
              <Button 
                variant="outline" 
                onClick={() => { fetchVersions(); setHistoryDialogOpen(true); }}
                data-testid="view-history-btn"
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Sheet Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {sheets.map((sheet) => (
          <Button
            key={sheet.id}
            variant={activeSheet === sheet.id ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveSheet(sheet.id)}
            className={activeSheet === sheet.id ? "bg-[#1e3a5f]" : ""}
          >
            <Table className="w-3 h-3 mr-1" />
            {sheet.name}
            {sheet.formulas_count > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {sheet.formulas_count}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Toolbar */}
      {sheetData && (
        <Card className="print:hidden">
          <CardContent className="py-2 px-4">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Cell Reference */}
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm bg-slate-100 px-3 py-1.5 rounded min-w-[60px] text-center font-bold">
                  {selectedCell || '-'}
                </span>
              </div>
              
              {/* Formula Bar */}
              <div className="flex-1 flex items-center gap-2 min-w-[300px]">
                <span className="text-slate-400 font-mono font-bold">fx</span>
                <Input
                  value={formulaBarValue}
                  onChange={(e) => handleFormulaBarChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleFormulaBarSubmit();
                    }
                  }}
                  placeholder={selectedCell ? 'Enter value or formula (start with =)' : 'Select a cell'}
                  className="font-mono"
                  disabled={!selectedCell || !hasPermission('edit_cells')}
                  data-testid="formula-bar"
                />
              </div>
              
              {/* Formula indicator */}
              {getCellFormula(selectedCell) && (
                <Badge className="bg-blue-100 text-blue-800">
                  <Calculator className="w-3 h-3 mr-1" />
                  Formula
                </Badge>
              )}
              
              {/* Formula Helper */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={!selectedCell || !hasPermission('edit_cells')}>
                    <Calculator className="w-4 h-4 mr-1" />
                    Insert Formula
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72">
                  <DropdownMenuLabel>Math Functions</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=SUM(A1:A10)')}>
                    <span className="font-mono text-xs mr-2">=SUM()</span>
                    <span className="text-slate-500">Add numbers in range</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=AVERAGE(A1:A10)')}>
                    <span className="font-mono text-xs mr-2">=AVERAGE()</span>
                    <span className="text-slate-500">Calculate average</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=MAX(A1:A10)')}>
                    <span className="font-mono text-xs mr-2">=MAX()</span>
                    <span className="text-slate-500">Find maximum value</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=MIN(A1:A10)')}>
                    <span className="font-mono text-xs mr-2">=MIN()</span>
                    <span className="text-slate-500">Find minimum value</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=ROUND(A1,2)')}>
                    <span className="font-mono text-xs mr-2">=ROUND()</span>
                    <span className="text-slate-500">Round to decimals</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=ABS(A1)')}>
                    <span className="font-mono text-xs mr-2">=ABS()</span>
                    <span className="text-slate-500">Absolute value</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Count Functions</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=COUNT(A1:A10)')}>
                    <span className="font-mono text-xs mr-2">=COUNT()</span>
                    <span className="text-slate-500">Count numbers</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=COUNTA(A1:A10)')}>
                    <span className="font-mono text-xs mr-2">=COUNTA()</span>
                    <span className="text-slate-500">Count non-empty cells</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Logic Functions</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=IF(A1>100,"Yes","No")')}>
                    <span className="font-mono text-xs mr-2">=IF()</span>
                    <span className="text-slate-500">Conditional logic</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Text Functions</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=CONCATENATE(A1,B1)')}>
                    <span className="font-mono text-xs mr-2">=CONCATENATE()</span>
                    <span className="text-slate-500">Join text</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=UPPER(A1)')}>
                    <span className="font-mono text-xs mr-2">=UPPER()</span>
                    <span className="text-slate-500">Convert to uppercase</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=LOWER(A1)')}>
                    <span className="font-mono text-xs mr-2">=LOWER()</span>
                    <span className="text-slate-500">Convert to lowercase</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=LEFT(A1,5)')}>
                    <span className="font-mono text-xs mr-2">=LEFT()</span>
                    <span className="text-slate-500">Extract left characters</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=RIGHT(A1,5)')}>
                    <span className="font-mono text-xs mr-2">=RIGHT()</span>
                    <span className="text-slate-500">Extract right characters</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=LEN(A1)')}>
                    <span className="font-mono text-xs mr-2">=LEN()</span>
                    <span className="text-slate-500">Count characters</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=TRIM(A1)')}>
                    <span className="font-mono text-xs mr-2">=TRIM()</span>
                    <span className="text-slate-500">Remove extra spaces</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Date Functions</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=TODAY()')}>
                    <span className="font-mono text-xs mr-2">=TODAY()</span>
                    <span className="text-slate-500">Current date</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFormulaBarChange('=NOW()')}>
                    <span className="font-mono text-xs mr-2">=NOW()</span>
                    <span className="text-slate-500">Current date & time</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Search */}
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-40"
                />
              </div>
              
              {/* Add Row/Column */}
              {hasPermission('edit_structure') && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Insert
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={handleAddRow}>
                      <PlusCircle className="w-4 h-4 mr-2" />
                      Add Row
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleAddColumn}>
                      <PlusCircle className="w-4 h-4 mr-2" />
                      Add Column
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Spreadsheet Grid */}
      {sheetData && (
        <Card className="print:shadow-none print:border-none overflow-hidden">
          <CardHeader className="pb-2 print:hidden border-b">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">{sheetData.name}</CardTitle>
                <CardDescription>
                  {sheetData.max_row} rows × {sheetData.max_col} columns • 
                  {sheetData.formulas_count > 0 && ` ${sheetData.formulas_count} formulas • `}
                  Version {sheetData.version}
                </CardDescription>
              </div>
              <div className="text-sm text-slate-500">
                Last updated: {new Date(sheetData.updated_at).toLocaleString()}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div 
              ref={tableContainerRef}
              className="overflow-auto" 
              style={{ maxHeight: 'calc(100vh - 380px)', maxWidth: '100%' }}
            >
              <table className="border-collapse" style={{ minWidth: `${maxCol * 100}px` }}>
                <thead className="sticky top-0 z-20">
                  <tr className="bg-[#1e3a5f]">
                    {/* Row number header */}
                    <th className="text-white font-semibold w-14 text-center border border-slate-400 sticky left-0 bg-[#1e3a5f] z-30">
                      #
                    </th>
                    {/* Column headers with resize handles */}
                    {columnHeaders.map((col) => (
                      <th 
                        key={col}
                        className="text-white font-semibold text-center border border-slate-400 px-1 py-2 relative group"
                        style={{ width: columnWidths[col] || 100, minWidth: 50 }}
                      >
                        {col}
                        {/* Resize handle */}
                        <div
                          className="absolute top-0 right-0 w-2 h-full cursor-col-resize opacity-0 group-hover:opacity-100 bg-blue-400 hover:bg-blue-500"
                          onMouseDown={(e) => handleColumnResizeStart(col, e)}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((rowNum) => (
                    <tr key={rowNum} className="group hover:bg-slate-50/50">
                      {/* Row number with resize handle */}
                      <td 
                        className="text-center text-slate-600 font-medium bg-slate-100 border border-slate-300 sticky left-0 z-10 relative"
                        style={{ height: rowHeights[rowNum] || 32 }}
                      >
                        {rowNum}
                        {/* Row resize handle */}
                        <div
                          className="absolute bottom-0 left-0 w-full h-1 cursor-row-resize opacity-0 group-hover:opacity-100 bg-blue-400 hover:bg-blue-500"
                          onMouseDown={(e) => handleRowResizeStart(rowNum, e)}
                        />
                      </td>
                      {/* Data cells */}
                      {columnHeaders.map((col) => {
                        const cellRef = `${col}${rowNum}`;
                        const isSelected = selectedCell === cellRef;
                        const isEdited = editedCells[cellRef];
                        const hasFormula = getCellFormula(cellRef);
                        const displayValue = getCellDisplay(cellRef);
                        const isFillPreview = isFillPreviewCell(cellRef);
                        
                        return (
                          <td 
                            key={cellRef}
                            className={`
                              border border-slate-200 p-0 relative
                              ${isSelected ? 'ring-2 ring-[#1e3a5f] ring-inset bg-blue-50' : ''}
                              ${isEdited ? 'bg-yellow-50' : ''}
                              ${hasFormula && !isEdited ? 'bg-green-50/30' : ''}
                              ${isFillPreview ? 'bg-blue-100 ring-1 ring-blue-400 ring-inset' : ''}
                            `}
                            style={{ 
                              width: columnWidths[col] || 100,
                              height: rowHeights[rowNum] || 32
                            }}
                            onClick={() => handleCellSelect(cellRef)}
                            onDoubleClick={() => {
                              const input = document.querySelector(`input[data-cell="${cellRef}"]`);
                              if (input) input.focus();
                            }}
                            onMouseEnter={(e) => handleFillMouseMove(e, cellRef)}
                            data-testid={`cell-${cellRef}`}
                          >
                            <input
                              type="text"
                              data-cell={cellRef}
                              value={isSelected ? formulaBarValue : displayValue}
                              onChange={(e) => {
                                if (isSelected) {
                                  handleFormulaBarChange(e.target.value);
                                }
                              }}
                              onFocus={() => handleCellSelect(cellRef)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const ref = parseCellRef(cellRef);
                                  if (ref) {
                                    handleCellSelect(`${ref.col}${ref.row + 1}`);
                                  }
                                } else if (e.key === 'Tab') {
                                  e.preventDefault();
                                  const ref = parseCellRef(cellRef);
                                  if (ref) {
                                    const nextCol = colLetterToNum(ref.col) + (e.shiftKey ? -1 : 1);
                                    if (nextCol >= 1) {
                                      handleCellSelect(`${colNumToLetter(nextCol)}${ref.row}`);
                                    }
                                  }
                                }
                              }}
                              className={`
                                w-full h-full px-2 border-0 bg-transparent text-sm
                                focus:outline-none focus:ring-0
                                ${!hasPermission('edit_cells') ? 'cursor-default' : ''}
                                ${typeof displayValue === 'number' || !isNaN(parseFloat(displayValue)) ? 'text-right' : 'text-left'}
                              `}
                              readOnly={!hasPermission('edit_cells')}
                            />
                            {/* Formula indicator */}
                            {hasFormula && (
                              <div className="absolute top-0 right-0 w-0 h-0 border-t-[6px] border-r-[6px] border-t-green-500 border-r-transparent" />
                            )}
                            {/* Edited indicator */}
                            {isEdited && (
                              <div className="absolute top-0 left-0 w-0 h-0 border-t-[6px] border-l-[6px] border-t-yellow-500 border-l-transparent" />
                            )}
                            {/* Fill Handle - small square at bottom-right of selected cell */}
                            {isSelected && hasPermission('edit_cells') && (
                              <div
                                ref={fillHandleRef}
                                className="absolute bottom-0 right-0 w-3 h-3 bg-[#1e3a5f] cursor-crosshair z-10 hover:bg-blue-600 transform translate-x-1/2 translate-y-1/2"
                                onMouseDown={handleFillHandleMouseDown}
                                title="Drag to fill cells"
                                style={{ 
                                  borderRadius: '1px',
                                  boxShadow: '0 0 0 1px white'
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No sheets message */}
      {sheets.length === 0 && !loading && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-8 text-center">
            <FileSpreadsheet className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-amber-800">No Account Sheets Found</h3>
            <p className="text-amber-600 mb-4">Initialize the sheets from the ICRCL Account Excel file or upload a new file.</p>
            {isMainAdmin && (
              <div className="flex gap-2 justify-center">
                <Button onClick={() => setUploadDialogOpen(true)} variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Excel
                </Button>
                <Button onClick={handleInitialize} disabled={initializing} className="bg-[#1e3a5f]">
                  <RefreshCw className={`w-4 h-4 mr-2 ${initializing ? 'animate-spin' : ''}`} />
                  {initializing ? 'Initializing...' : 'Initialize'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5" />
              Upload Excel File
            </DialogTitle>
            <DialogDescription>
              Upload a new Excel file to replace the current account data. All formulas will be preserved.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">Drag and drop an Excel file here, or click to browse</p>
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls"
                onChange={handleUpload}
                className="hidden"
                id="excel-upload"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                variant="outline"
              >
                {uploading ? 'Uploading...' : 'Choose File'}
              </Button>
            </div>
            
            <div className="text-sm text-slate-500">
              <p>Supported formats: .xlsx, .xls</p>
              <p>The file will replace the current ICRCL_Account.xlsx</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Manage Sheet Permissions
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Grant or revoke permissions for users to access this sheet.
            </p>
            
            {permissions.length === 0 ? (
              <p className="text-sm text-slate-500">No permissions granted yet.</p>
            ) : (
              <div className="space-y-2">
                {permissions.map((perm) => (
                  <Card key={perm.user_id} className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{perm.user_name}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {perm.permissions?.map((p) => (
                            <Badge key={p} variant="outline" className="text-xs">{PERMISSION_LABELS[p] || p}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Version History
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2">
            {versions.length === 0 ? (
              <p className="text-sm text-slate-500">No version history available.</p>
            ) : (
              versions.map((v) => (
                <Card key={v.version} className="p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">Version {v.version}</p>
                      <p className="text-sm text-slate-500">{v.change_summary}</p>
                      <p className="text-xs text-slate-400">
                        By {v.changed_by} • {new Date(v.changed_at).toLocaleString()}
                      </p>
                    </div>
                    <Badge variant="outline">{v.change_type}</Badge>
                  </div>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge Cells Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Combine className="w-5 h-5" />
              Merge Cells
            </DialogTitle>
            <DialogDescription>
              Merge a range of cells. The value from the top-left cell will be kept.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Cell</Label>
                <Input
                  value={mergeStartCell}
                  onChange={(e) => setMergeStartCell(e.target.value.toUpperCase())}
                  placeholder="e.g., A1"
                  data-testid="merge-start-cell"
                />
              </div>
              <div>
                <Label>End Cell</Label>
                <Input
                  value={mergeEndCell}
                  onChange={(e) => setMergeEndCell(e.target.value.toUpperCase())}
                  placeholder="e.g., C3"
                  data-testid="merge-end-cell"
                />
              </div>
            </div>
            
            <p className="text-sm text-slate-500">
              This will merge cells from {mergeStartCell || '?'} to {mergeEndCell || '?'}
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMergeCells} 
              disabled={merging || !mergeStartCell || !mergeEndCell}
              className="bg-[#1e3a5f]"
              data-testid="confirm-merge-btn"
            >
              {merging ? 'Merging...' : 'Merge Cells'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Autofit Dialog */}
      <Dialog open={autofitDialogOpen} onOpenChange={setAutofitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Columns className="w-5 h-5" />
              Autofit Columns/Rows
            </DialogTitle>
            <DialogDescription>
              Automatically adjust column widths or row heights to fit content.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Autofit Type</Label>
              <Select value={autofitType} onValueChange={setAutofitType}>
                <SelectTrigger data-testid="autofit-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_columns">All Columns</SelectItem>
                  <SelectItem value="all_rows">All Rows</SelectItem>
                  <SelectItem value="all">All Columns & Rows</SelectItem>
                  <SelectItem value="column">Specific Column</SelectItem>
                  <SelectItem value="row">Specific Row</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(autofitType === 'column' || autofitType === 'row') && (
              <div>
                <Label>{autofitType === 'column' ? 'Column Letter' : 'Row Number'}</Label>
                <Input
                  value={autofitTarget}
                  onChange={(e) => setAutofitTarget(e.target.value)}
                  placeholder={autofitType === 'column' ? 'e.g., A' : 'e.g., 5'}
                  data-testid="autofit-target"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutofitDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAutofit} 
              disabled={autofitting || ((autofitType === 'column' || autofitType === 'row') && !autofitTarget)}
              className="bg-[#1e3a5f]"
              data-testid="confirm-autofit-btn"
            >
              {autofitting ? 'Applying...' : 'Apply Autofit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
